import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import DisableTrialPlanPermissions from 'permission/disable-trial-plan-permissions';

// Dashboard
const Dashboard = Loadable(lazy(() => import('views/dashboard')));

// Assessments
const Assessments = Loadable(lazy(() => import('views/assessments')));
const AssessmentInfo = Loadable(lazy(() => import('views/assessments/AssessmentInfo')));
const EvidenceLibrary = Loadable(lazy(() => import('views/assessments/EvidenceLibrary')));

// Candidates
const Candidates = Loadable(lazy(() => import('views/candidates')));
const CandidateInfo = Loadable(lazy(() => import('views/candidates/candidate-info')));
const InviteCandidate = Loadable(lazy(() => import('views/candidates/InviteCandidate')));

// Courses
const Courses = Loadable(lazy(() => import('views/courses')));
const CreateCourse = Loadable(lazy(() => import('views/courses/CreateCourse')));
const EditCourse = Loadable(lazy(() => import('views/courses/EditCourse')));
const ContinueDraftCourse = Loadable(lazy(() => import('views/courses/ContinueDraftCourse')));
const ViewCourse = Loadable(lazy(() => import('views/courses/ViewCourse')));

// reports
const Reports = Loadable(lazy(() => import('views/reports')));

// Settings
const Settings = Loadable(lazy(() => import('views/settings')));

const Error404 = Loadable(lazy(() => import('views/error/Error404')));

const MainRoutes = {
    path: '/',
    errorElement: <Error404 />,
    element: (
        <AuthGuard allowedPrincipalTypes={['user']}>
            <MainLayout />
        </AuthGuard>
    ),
    children: [
        {
            path: '/dashboard',
            element: <Dashboard />
        },
        {
            path: '/assessments',
            children: [
                {
                    path: '',
                    element: <Assessments />
                },
                {
                    path: 'info/:assessmentCode',
                    element: <AssessmentInfo />
                },
            ]
        },
        {
            path: '/candidates',
            children: [
                {
                    path: '',
                    element: <Candidates />
                },
                {
                    path: 'info/:id',
                    element: <CandidateInfo />
                },
                {
                    path: 'info/:id/evidence-library',
                    element: <EvidenceLibrary />
                },
                {
                    path: 'info/:id/evidence-library/deleted',
                    element: <EvidenceLibrary />
                },
                {
                    path: 'invite/:candidateId?',
                    element: <InviteCandidate />
                }
            ]
        },
        {
            path: '/courses',
            children: [
                {
                    path: '',
                    element: <Courses />
                },
                {
                    path: 'new',
                    element: <CreateCourse />
                },
                {
                    path: 'edit/:id',
                    element: <EditCourse />
                },
                {
                    path: 'continue-draft/:id/:checkStatus?',
                    element: <ContinueDraftCourse />
                },
                {
                    path: 'view/:id',
                    element: <ViewCourse />
                }
            ]
        },
        {
            path: '/settings',
            element: <Settings />
        },
        {
            path: '/analytics',
            element: (
                <DisableTrialPlanPermissions
                    fullPage
                    trialContent={{
                        title: 'Analytics is available on paid plans',
                        description:
                            'Upgrade to a paid plan to access reporting insights, assessment analytics, and performance trends across your account.'
                    }}
                >
                    <Reports />
                </DisableTrialPlanPermissions>
            )
        }
    ]
};

export default MainRoutes;
